import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import { can_visit, exchangeDate, getPractice, notify, usDate, usPhoneFormat } from "../../helpers";
import Spacing from "../Spacing";
import { acceptInvoice, fetchSingleInvoice } from "../../services/billing";
import { Config } from "../../Config";
import { CreditCard, Loader } from "lucide-react";

export default function InvoicePrint({ ...props }) {
  const {    
    formViewId,
    formViewSize = "A4",
    setFormView = () => {},
    filterData = {},
    reloadFunction = () => {}
  } = props;

  const [invoiceData, setInvoiceData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [createInvoiceLoader, setCreateInvoiceLoader] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = getPractice("account_id");
  const locationId = getPractice("location_id");

  let formId = formViewId;
  let formSize = formViewSize;
  if (queryParams.get('form_id')) {
    formId = queryParams.get('form_id');
    formSize = queryParams.get('size');
  }

  useEffect(() => {
    if(formId !== null){
      getInvoiceData( formId );
    } else {
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const getInvoiceData = async ( formId ) => {
    const response = await fetchSingleInvoice( accountId, locationId, formId );
    if (response) {
      if(response.data === null) {
        window.close();
      }
      setInvoiceData(response?.data);
      setIsDataLoaded(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (isDataLoaded && queryParams.get('form_id')) {
      // handlePrint();
    }
  }, [isDataLoaded, queryParams]);

  const handleCloseWindow = () => {
    window.close();
  };

  const handleFormPrint = (formId) => {
    setFormView();
    const url = `/invoice/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const pageSize = formSize == "A4" ? "A4" : "Letter";
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      @media print {
        @page {
          size: ${pageSize};
        }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [formSize]);

  const handleAcceptPayment = async (invoiceId) => {
    setCreateInvoiceLoader(true);
    try {
      const acceptInvoiceData = {
        account_id: accountId,
        location_id: locationId,
        invoice_id: invoiceId
      };
      const result = await acceptInvoice(acceptInvoiceData);

      if(result){
        if(result?.detail){
          notify("error", result?.detail, 2);
          return;
        }
        if(result?.data?.status === 'success'){
          setIsDataLoaded(false);
          reloadFunction(filterData);
          getInvoiceData(result?.data?.invoice_id);
          notify("success", result?.data?.message, 0.5);
        } else {
          notify("error", result?.data?.message, 2);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setCreateInvoiceLoader(false);
    }
  };

  useDocumentTitle("Invoice View");

  return (
    <div ref={contentRef} className="docpro_signout_wrap">
      <div className="docpro_signout flex docpro_height_100vh" style={{ flex: '1 1', flexDirection: 'column' }}>
        {!isDataLoaded ? (
          <div style={{ height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
            Processing...
          </div>
        ) : (
          <>
            <div className='w-full'>
              <div className="docpro_signout_header mb-0" style={{ display: 'flex', justifyContent: 'space-between', height: '70px', borderBottom: 0, flexDirection: 'row' }}>
                <div className="docpro_signout_logo">
                  <img 
                    src="/images/logo.svg" 
                    alt="Logo" 
                    style={{ height: "36px" }}
                  />
                </div>
                <div>
                  <span className={`docpro_badge ${invoiceData?.invoice?.status === "paid" ? 'docpro_badge_green' : 'docpro_badge_red'} docpro_fs_16 px-3 py-1 docpro_square`} style={{ textTransform: 'capitalize' }} >
                    {invoiceData?.invoice?.status}
                  </span>
                </div>
              </div>
              <h4 className="docpro_mb_0">Invoice: {invoiceData?.invoice?.invoice_number}</h4>
              <Spacing lg="7" sm="7" />
              <p className="docpro_mb_0">
                {invoiceData?.invoice_request?.billing_date !== null ? 
                  <><b>Billing date: </b> {exchangeDate(invoiceData?.invoice_request?.billing_date)}</>
                : 
                  <><b>Invoice date: </b> {usDate(invoiceData?.payment_intent?.updated_at)}</>
                }
              </p>
              <Spacing lg="30" sm="30" />
              <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2">
                <div className="docpro_grid_item">
                  <p className="docpro_fs_24 docpro_ternary_color">From:</p>
                  <p className="docpro_mb_0 docpro_fs_16"><b>{Config._AppName}</b></p>
                  <p className="docpro_mb_0">{Config._AppAddress}</p>
                  <p className="docpro_mb_0">{Config._AppPhone}</p>
                  <p className="docpro_mb_0">{Config._AppEmail}</p>
                </div>

                <div className="docpro_grid_item">
                  <p className="docpro_fs_24 docpro_ternary_color">To:</p>
                  {invoiceData?.customer?.id !== null ? (
                    <>
                      <p className="docpro_mb_0 docpro_fs_16"><b>{invoiceData?.customer?.practice_name}</b></p>
                      <p className="docpro_mb_0">{usPhoneFormat(invoiceData?.customer?.phone)}</p>
                      <p className="docpro_mb_0">{invoiceData?.customer?.email}</p>
                    </>
                  ) : (
                    <>
                      <p className="docpro_mb_0 docpro_fs_16"><b>{getPractice("account_name")}</b></p>
                      <p className="docpro_mb_0">{getPractice("location_name")}</p>
                    </>
                  )}
                </div>
              </div>
              <Spacing lg="35" sm="35" />
              {/* <h4 className="docpro_mb_0 docpro_fs_16 mb-1">Invoice Items</h4> */}
              <div className="docpro_table_1 hipaa_table invoice docpro_table_responsive" style={{ boxShadow: 'none', borderRadius: 0, overflowX: 'auto' }}>
                <table className="docpro_mb_0" style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <th>
                        <b>Description</b>
                      </th>
                      <th className="text-right" style={{ width: '120px' }}>
                        <b>{invoiceData?.invoice?.invoice_type !== "cc_billing" ? 'Qty' : 'Total Done'}</b>
                      </th>
                      <th className="text-right" style={{ width: '200px' }}>
                        <b>{invoiceData?.invoice?.invoice_type !== "cc_billing" ? 'Amount' : 'Charge'}</b>
                      </th>
                    </tr>
                    {invoiceData?.invoice_request?.id === null ?
                      <tr>
                        <td>{invoiceData?.invoice?.description}</td>
                        <td className="text-right">{invoiceData?.checkout_session?.quantity ? invoiceData?.checkout_session?.quantity : 1}</td>
                        <td className="text-right">
                          {invoiceData?.invoice?.amount_paid ? `$${(invoiceData?.invoice?.amount_paid / invoiceData?.checkout_session?.quantity)?.toFixed(2)}` : ''}
                        </td>
                      </tr>
                    :
                      <>
                        {invoiceData?.invoice_request?.coding_quantity !== null && (
                          <tr>
                            <td style={{ verticalAlign: 'unset' }}>{invoiceData?.invoice_request?.coding_product_name} {invoiceData?.invoice_request?.coding_price_type !==  "DEFAULT" && (<small className='docpro_red_color'>({invoiceData?.invoice_request?.coding_price_type})</small>) }</td>
                            <td style={{ verticalAlign: 'unset' }} className="text-right">{invoiceData?.invoice_request?.coding_quantity}</td>
                            <td className="text-right">
                              {`$${(invoiceData?.invoice_request?.coding_quantity * invoiceData?.invoice_request?.coding_price_amount)?.toFixed(2)}`}
                              <br/>
                              <small className='docpro_ternary_color'>${invoiceData?.invoice_request?.coding_price_amount?.toFixed(2) } each</small>
                            </td>
                          </tr>
                        )}
                        {invoiceData?.invoice_request?.charting_quantity !== null && (
                          <tr>
                            <td style={{ verticalAlign: 'unset' }}>{invoiceData?.invoice_request?.charting_product_name} {invoiceData?.invoice_request?.charting_price_type !==  "DEFAULT" && (<small className='docpro_red_color'>({invoiceData?.invoice_request?.charting_price_type})</small>) }</td>
                            <td style={{ verticalAlign: 'unset' }} className="text-right">{invoiceData?.invoice_request?.charting_quantity}</td>
                            <td className="text-right">
                              {`$${(invoiceData?.invoice_request?.charting_quantity * invoiceData?.invoice_request?.charting_price_amount)?.toFixed(2)}`}
                              <br/>
                              <small className='docpro_ternary_color'>${invoiceData?.invoice_request?.charting_price_amount?.toFixed(2) } each</small>
                            </td>
                          </tr>
                        )}
                      </>
                    }
                    <tr>
                      <td className="text-right" colSpan={2}>
                        <b>Sub Total</b>
                      </td>
                      <td className="text-right">
                        <b>{invoiceData?.invoice?.amount_paid ? `$${invoiceData?.invoice?.amount_paid.toFixed(2)}` : ''}</b>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right" style={{ borderTop: 0 }} colSpan={2}>
                        <b>Discount</b>
                      </td>
                      <td style={{ borderTop: 0 }}>
                        <p className="docpro_mb_0 text-right docpro_red_color">{invoiceData?.checkout_session?.discount_paid ? `-$${invoiceData?.checkout_session?.discount_paid?.toFixed(2)}` : '-$0.00'}</p>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ borderTop: 0 }}></td>
                      <td className="text-right">
                        <b>Total</b>
                      </td>
                      <td className="text-right">
                        <b>{invoiceData?.invoice?.amount_paid ? `$${invoiceData?.invoice?.amount_paid.toFixed(2)}` : ''}</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {invoiceData?.payment_method?.id !== null &&
                <>
                  <Spacing lg="35" sm="35" />
                  <h4 className="docpro_mb_0 docpro_fs_16 mb-1">Transactions</h4>
                  <div className="docpro_table_1 hipaa_table invoice docpro_table_responsive" style={{ boxShadow: 'none', borderRadius: 0, overflowX: 'auto' }}>
                    <table className="docpro_mb_0" style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <th>
                            <b>Transaction Date</b>
                          </th>
                          <th>
                            <b>Gateway</b>
                          </th>
                          <th>
                            <b>Transaction ID</b>
                          </th>
                          <th>
                            <b>Amount</b>
                          </th>
                        </tr>
                        <tr>
                          <td>{usDate(invoiceData?.payment_intent?.updated_at)}</td>
                          <td>{invoiceData?.payment_method?.card_brand} •••• {invoiceData?.payment_method?.card_last4}</td>
                          <td>{invoiceData?.payment_intent?.id}</td>
                          <td>
                            {invoiceData?.invoice?.amount_paid ? `$${invoiceData?.invoice?.amount_paid?.toFixed(2)}` : ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              }

              {invoiceData?.subscription?.canceled_at !== null &&
                <>
                  <Spacing lg="35" sm="35" />
                  <div className="docpro_table_1 hipaa_table invoice docpro_table_responsive" style={{ boxShadow: 'none', borderRadius: 0, overflowX: 'auto' }}>
                    <table className="docpro_mb_0" style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <th>
                            <b>Canceled Date</b>
                          </th>
                          <th>
                            <b>Reason</b>
                          </th>
                        </tr>
                        <tr>
                          <td>{usDate(invoiceData?.subscription?.canceled_at)}</td>
                          <td>{invoiceData?.subscription?.cancel_comment}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              }
            </div>

            {!queryParams.get('form_id') &&
            (invoiceData?.invoice?.status !== "paid" && invoiceData?.invoice_request?.id !== null && can_visit("billing") && can_visit("billing_accept_and_pay")) && (
              <div className='text-center page_divider'>
                <Spacing lg="50" sm="50" />
                <button
                  type="button"
                  style={{ borderRadius: '4px', width: 'auto', height: 'auto' }}
                  className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn docpro_icon_btn overview pay"
                  onClick={() => handleAcceptPayment(invoiceData?.invoice?.id)}
                  disabled={createInvoiceLoader ? true : false}
                >
                  <CreditCard width={20} height={20} />
                  <span>Accept Invoice</span>
                  <span className="docpro_tip">Authorize and approve the payment process.</span>
                  {createInvoiceLoader && (
                    <span className="docpro_loading">
                      <Loader width={20} height={20} />
                    </span>
                  )}
                </button>
                <Spacing lg="20" sm="20" />
                <p className="docpro_ternary_color">Click <b  className="docpro_ternary_color">Accept Invoice</b> to authorize the payment of this invoice. The amount will be securely and automatically charged to your previously saved payment card.</p>
                
              </div>
            )}
          </>
        )}
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={() => {
            if (queryParams.get('form_id')) {
              handleCloseWindow();
            } else {
              setFormView();
            }
          }}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        {invoiceData?.invoice?.invoice_pdf !== null ? (
          <a href={invoiceData?.invoice?.invoice_pdf} download rel="noreferrer">
            <button
              className="docpro_invoice_btn docpro_color1"
            >
              <span className="docpro_btn_icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" x2="12" y1="15" y2="3"/>
                </svg>
              </span>
              <span className="docpro_btn_text">Download</span>
            </button>
          </a>
        ) : (
          <button
            className="docpro_invoice_btn docpro_color1"
            onClick={() => {
              if (queryParams.get('form_id')) {
                handlePrint();
              } else {
                handleFormPrint(formViewId);
              }
            }}
          >
            <span className="docpro_btn_icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512" >
                <path d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H104a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
                <rect x={128} y={240} width={256} height={208} rx="24.32" ry="24.32" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
                <path d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
                <circle cx={392} cy={184} r={24} fill="currentColor" /> 
              </svg>
            </span>
            <span className="docpro_btn_text">Print</span>
          </button>
        )}
      </div>
    </div>
  );
}
